import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchCompanies } from "../redux/companiesSlice";
import type { Company } from "../redux/companiesSlice";
import { ArrowUpRight } from "lucide-react";

/* ─── Font ─── */
const fontHref = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = fontHref;
  document.head.appendChild(l);
}

/* ─── Tokens ─── */
const C = {
  bg:      "#F8F7F5",
  white:   "#FFFFFF",
  black:   "#0A0A0A",
  ink:     "#1A1A1A",
  gray:    "#6B6B6B",
  grayLt:  "#CCCCCC",
  border:  "#E5E3DF",
  red:     "#E50914",
  redDark: "#B00710",
};

/* ════════════════════════════════════════
   COMPANIES PAGE
════════════════════════════════════════ */
export default function Companies() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { companies, status } = useSelector((s: RootState) => s.companies);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filter,   setFilter]   = useState("All");

  useEffect(() => { dispatch(fetchCompanies()); }, [dispatch]);

  const published = useMemo(() => companies.filter(c => c.published), [companies]);

  const industries = useMemo(() => {
    const set = new Set(published.map(c => c.industry).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [published]);

  const filtered = useMemo(() =>
    filter === "All" ? published : published.filter(c => c.industry === filter),
    [published, filter]
  );

  const toggle = (id: string) => setExpanded(prev => prev === id ? null : id);

  return (
    <>
      <Helmet>
        <title>Companies — 4REVA GROUP</title>
        <meta name="description" content="The 4REVA GROUP portfolio — technology brands across e-commerce, fintech, media, beauty and security." />
      </Helmet>

      <div style={{ backgroundColor: C.bg, minHeight: "100vh", color: C.ink, fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── NAV ── */}
        <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, background: "rgba(248,247,245,0.97)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${C.border}`, padding: "0 6%", height: 68, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <motion.div whileHover={{ scale: 1.02 }} onClick={() => navigate("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Serif Display', serif", fontSize: 13, color: C.white }}>SG</div>
            <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 16, letterSpacing: "0.2em", color: C.black }}>4REVA GROUP</span>
          </motion.div>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: C.gray, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.06em" }}>← Home</button>
            <motion.button whileHover={{ scale: 1.03 }} onClick={() => navigate("/contact")}
              style={{ background: C.red, color: C.white, border: "none", padding: "10px 22px", fontWeight: 700, fontSize: 12, letterSpacing: "0.12em", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
              CONTACT US
            </motion.button>
          </div>
        </nav>

        {/* ── HEADER ── */}
        <div style={{ paddingTop: 68, borderBottom: `1px solid ${C.border}`, background: C.white }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "80px 6% 60px" }}>
            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              style={{ fontSize: 11, letterSpacing: "0.4em", color: C.red, fontWeight: 700, textTransform: "uppercase", marginBottom: 16 }}>
              Our Portfolio
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(36px, 5vw, 72px)", fontWeight: 400, lineHeight: 1.05, margin: "0 0 24px", color: C.black }}>
              The Companies
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
              style={{ fontSize: 15, lineHeight: 1.8, color: C.gray, maxWidth: 480, margin: 0 }}>
              A portfolio of technology brands built to lead their categories.
            </motion.p>
          </div>
        </div>

        {/* ── FILTER PILLS ── */}
        {industries.length > 1 && (
          <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, overflowX: "auto" }}>
            <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 6%", display: "flex", gap: 4, height: 52, alignItems: "center" }}>
              {industries.map(ind => (
                <button key={ind} onClick={() => setFilter(ind)}
                  style={{
                    background: filter === ind ? C.black : "transparent",
                    color: filter === ind ? C.white : C.gray,
                    border: `1px solid ${filter === ind ? C.black : C.border}`,
                    padding: "5px 16px", fontSize: 10, fontWeight: 700,
                    letterSpacing: "0.12em", cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase",
                    whiteSpace: "nowrap", transition: "all 0.2s",
                  }}>
                  {ind}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── COMPANY LIST ── */}
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 6% 100px" }}>

          {status === "loading" && (
            <div style={{ textAlign: "center", padding: "80px 0", color: C.gray, fontSize: 14 }}>
              Loading companies…
            </div>
          )}

          {status !== "loading" && filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: C.black, margin: "0 0 12px" }}>Check back soon</p>
              <p style={{ fontSize: 13, color: C.gray, margin: 0 }}>No companies published yet.</p>
            </div>
          )}

          <div style={{ border: `1px solid ${C.border}`, background: C.white }}>
            {filtered.map((co, i) => (
              <CompanyRow
                key={co.id}
                company={co}
                index={i}
                isExpanded={expanded === co.id}
                onToggle={() => toggle(co.id)}
                isLast={i === filtered.length - 1}
              />
            ))}
          </div>
        </div>

        {/* ── FOOTER ── */}
        <footer style={{ background: C.black, borderTop: `1px solid ${C.border}`, padding: "40px 6%", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", margin: 0 }}>
            © {new Date().getFullYear()} 4REVA GROUP · Reg. 17381555 · Estonia, EU
          </p>
          <div style={{ display: "flex", gap: 24 }}>
            {[{ l: "Privacy", p: "/privacy" }, { l: "Terms", p: "/terms" }, { l: "Home", p: "/" }].map(x => (
              <span key={x.l} onClick={() => navigate(x.p)}
                style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", cursor: "pointer", transition: "color 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.color = "#fff")}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}>
                {x.l}
              </span>
            ))}
          </div>
        </footer>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .co-expand-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}

/* ════════════════════════════════════════
   COMPANY ROW
════════════════════════════════════════ */
function CompanyRow({ company, index, isExpanded, onToggle, isLast }: {
  company: Company;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  isLast: boolean;
}) {
  const [hov, setHov] = useState(false);

  return (
    <div style={{ borderBottom: isLast && !isExpanded ? "none" : `1px solid ${C.border}` }}>

      {/* ── Collapsed row ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.05 }}
        onClick={onToggle}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: "flex", alignItems: "center", gap: 20,
          padding: "20px 24px", cursor: "pointer",
          background: isExpanded ? C.bg : hov ? "#FAFAF8" : C.white,
          borderLeft: `3px solid ${isExpanded ? C.red : "transparent"}`,
          transition: "background 0.2s, border-color 0.2s",
        }}>

        {/* Index */}
        <span style={{ fontSize: 11, color: C.grayLt, fontWeight: 700, letterSpacing: "0.08em", minWidth: 28, flexShrink: 0 }}>
          {String(index + 1).padStart(2, "0")}
        </span>

        {/* Logo thumb */}
        <div style={{ width: 44, height: 44, flexShrink: 0, background: C.bg, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          {company.logoUrl
            ? <img src={company.logoUrl} alt={company.name} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 6 }} />
            : <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 14, color: C.grayLt }}>{company.name.slice(0, 2)}</span>
          }
        </div>

        {/* Name + tagline */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, color: C.black, letterSpacing: "0.01em", lineHeight: 1.2 }}>
            {company.name}
          </div>
          {company.tagline && (
            <div style={{ fontSize: 12, color: C.gray, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {company.tagline}
            </div>
          )}
        </div>

        {/* Industry tag */}
        {company.industry && (
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.16em", color: C.gray, background: C.bg, border: `1px solid ${C.border}`, padding: "4px 12px", textTransform: "uppercase", flexShrink: 0 }}>
            {company.industry}
          </span>
        )}

        {/* Toggle */}
        <motion.div
          animate={{ rotate: isExpanded ? 45 : 0 }}
          transition={{ duration: 0.22 }}
          style={{ width: 28, height: 28, border: `1px solid ${isExpanded ? C.red : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "border-color 0.2s" }}>
          <span style={{ fontSize: 18, color: isExpanded ? C.red : C.gray, lineHeight: 1, marginTop: -1 }}>+</span>
        </motion.div>
      </motion.div>

      {/* ── Expanded panel ── */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: "hidden" }}>

            <div className="co-expand-grid" style={{
              display: "grid",
              gridTemplateColumns: "260px 1fr",
              borderTop: `1px solid ${C.border}`,
              background: C.bg,
            }}>

              {/* Left: logo + meta + CTA */}
              <div style={{ padding: "28px 24px", borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 20 }}>

                {/* Logo large */}
                <div style={{ width: "100%", aspectRatio: "4/3", background: C.white, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                  {company.logoUrl
                    ? <img src={company.logoUrl} alt={company.name} style={{ maxWidth: "65%", maxHeight: "65%", objectFit: "contain" }} />
                    : <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, color: C.grayLt }}>{company.name.slice(0, 2)}</span>
                  }
                </div>

                {/* Meta */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[
                    { label: "Industry", value: company.industry },
                    { label: "Founded",  value: company.founded },
                    { label: "Location", value: company.location },
                  ].filter(m => m.value).map(m => (
                    <div key={m.label}>
                      <div style={{ fontSize: 9, letterSpacing: "0.2em", color: C.grayLt, fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>{m.label}</div>
                      <div style={{ fontSize: 13, color: C.ink }}>{m.value}</div>
                    </div>
                  ))}
                </div>

                {/* Visit CTA */}
                {company.website && (
                  <motion.a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02, background: C.redDark }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      background: C.red, color: C.white, textDecoration: "none",
                      padding: "12px 20px", fontWeight: 700, fontSize: 11,
                      letterSpacing: "0.14em", fontFamily: "'DM Sans', sans-serif",
                      marginTop: "auto", transition: "background 0.2s",
                    }}>
                    VISIT SITE
                    <ArrowUpRight size={13} />
                  </motion.a>
                )}
              </div>

              {/* Right: cover + description */}
              <div style={{ display: "flex", flexDirection: "column" }}>

                {/* Cover image */}
                {company.coverImage ? (
                  <div style={{ width: "100%", aspectRatio: "16/7", overflow: "hidden" }}>
                    <img src={company.coverImage} alt={company.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                ) : (
                  <div style={{ width: "100%", aspectRatio: "16/7", background: C.border, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 48, color: "rgba(0,0,0,0.07)", letterSpacing: "0.1em" }}>{company.name}</span>
                  </div>
                )}

                {/* Description */}
                <div style={{ padding: "28px 32px", flex: 1, background: C.white }}>
                  <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, fontWeight: 400, color: C.black, margin: "0 0 14px" }}>
                    {company.name}
                  </h2>
                  <p style={{ fontSize: 14, lineHeight: 1.85, color: C.gray, margin: 0, maxWidth: 560 }}>
                    {company.description}
                  </p>

                  {/* Media gallery strip */}
                  {company.mediaUrls?.length > 0 && (
                    <div style={{ display: "flex", gap: 8, marginTop: 24, overflowX: "auto", paddingBottom: 4 }}>
                      {company.mediaUrls.map((url, i) => (
                        <img key={i} src={url} alt="" style={{ height: 72, width: 108, objectFit: "cover", flexShrink: 0, border: `1px solid ${C.border}` }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}